import React from "react";
import useBasketPackExtras from "../../../../../hooks/shared/extras/useBasketPackExtras";
import BasketItemWrapper from "./BasketItemWrapper";
import BasketItemRow from "./BasketItemRow";
import {
    removeExtra,
    removePack,
    removePackExtra,
    removeWinterWheel,
} from "../../../../../redux/actions/BasketActions";
import { type BuildAndBuyDispatchType, useBnbSelector } from "../../../../../redux/store";
import { find } from "../../../../../../../../common-deprecated/utils";
import { calculateWinterWheelPrice, getCarPrice } from "../../../../../utils/typedUtils";
import * as Styles from "../../../../../../../../common-deprecated/styles/v2/toyota/custom/basket/BasketList";
import type { UpholsteryType } from "../../../../../../../../common-deprecated/types/CommonTypes";
import CarDataConsolidator, { UPHOLSTERY } from "../../../../../../../shared-logic/utils/classes/CarDataConsolidator";
import { formatPrice } from "../../../../../../../../common-deprecated/Globalize";
import { shouldDisableforMonthlyRate } from "../../../../../../../shared-logic/utils/packUtils";
import { trackBasketExpandPanel, trackBasketRemoveExtra } from "../../../../../utils/tracking";
import { showPrice } from "../../../../../../../../common-deprecated/settings/utils/financeSettingUtilsCommon";
import { showRetailPriceMonthly } from "../../../../../../../../common-deprecated/settings/utils/commonSettingUtils";
import { getBnbLabel, useBnbLabel } from "../../../../../utils/labels";
import { getPriceFromExtra } from "../../../../../../../shared-logic/utils/priceUtils";
import { useDispatch } from "../../../../../../../../shared-logic/redux/store";

type PropsType = {
    displayZeroPriceWithStandard?: boolean;
    showVehiclePrice?: boolean;
};

const BasketItems = (props: PropsType): JSX.Element => {
    const { displayZeroPriceWithStandard, showVehiclePrice } = props;
    const dispatch = useDispatch<BuildAndBuyDispatchType>();

    const commonSettings = useBnbSelector((state) => state.commonSettings);
    const bnbSettings = useBnbSelector((state) => state.bnbSettings);
    const basket = useBnbSelector((state) => state.basket);
    const upholstery = useBnbSelector((state) =>
        CarDataConsolidator.getPersonalizationValue(UPHOLSTERY, state.basket, state.car),
    ) as UpholsteryType;
    const financeOption = useBnbSelector((state) => state.utils.financeOption);
    const carPrice = useBnbSelector((state) => getCarPrice(state.basket, state.car, state.externalPrice));
    const hideWheelPrice = useBnbSelector((state) => state.bnbSettings.disableBuildAccessories);
    const step = useBnbSelector((state) => state.navigation.step);

    const { colour, wheel, winterWheel } = basket;
    const cultureName = commonSettings.culture.name;
    const showPrices = showPrice(commonSettings);
    const showRetailPriceMonthlySetting = showRetailPriceMonthly(commonSettings);
    const basketProps = useBasketPackExtras();
    const { extras, packs, packExtras, getPackName, getExtraName, totalExtraPrice, totalPackPrice } = basketProps;

    const standardLabel = useBnbLabel("standard");
    const formattedStandardLabel = displayZeroPriceWithStandard
        ? `${standardLabel} (${formatPrice(0, cultureName)})`
        : standardLabel;

    return (
        <>
            <div dir="ltr">
                <Styles.BasketList my={3}>
                    {showVehiclePrice && showPrices && (financeOption === "cash" || showRetailPriceMonthlySetting) && (
                        <BasketItemRow
                            name={getBnbLabel({ commonSettings, bnbSettings }, "basketRetailPrice")}
                            price={carPrice}
                            financeOption="cash" // we want to force cash here since this line is only shown on monthly for TGB and they want it to display cash
                        />
                    )}
                    <BasketItemRow
                        name={colour.name}
                        price={showPrices ? colour.price : undefined}
                        priceFallback={showPrices ? formattedStandardLabel : undefined}
                        itemId={colour.id}
                    />
                    <BasketItemRow
                        name={upholstery.name}
                        price={showPrices ? upholstery.price : undefined}
                        priceFallback={showPrices ? formattedStandardLabel : undefined}
                        itemId={upholstery.id}
                    />
                    <BasketItemRow
                        name={wheel.name}
                        price={showPrices && !hideWheelPrice ? wheel.price : undefined}
                        priceFallback={showPrices && !hideWheelPrice ? formattedStandardLabel : undefined}
                        itemId={wheel.id}
                    />
                    {winterWheel && (
                        <BasketItemRow
                            name={winterWheel.name}
                            price={showPrices ? calculateWinterWheelPrice(winterWheel) : undefined}
                            itemId={winterWheel.id}
                            tooltip={
                                winterWheel.installTheWheels && !winterWheel.installationCost
                                    ? "winterWheelsModalExcludingInstallationCosts"
                                    : undefined
                            }
                            removeItem={() => {
                                trackBasketRemoveExtra(step);
                                dispatch(removeWinterWheel());
                            }}
                        />
                    )}
                </Styles.BasketList>

                {packs.length + packExtras.length > 0 && (
                    <BasketItemWrapper
                        sectionTitleLabel={getBnbLabel({ commonSettings, bnbSettings }, "basketPacks")}
                        totalPrice={totalPackPrice}
                        itemCount={packs.length + packExtras.length}
                        trackExpand={() => trackBasketExpandPanel(step, "expand-packs")}
                    >
                        <Styles.BasketList>
                            {packs.map((pack) => (
                                <BasketItemRow
                                    key={pack.id}
                                    price={showPrices ? pack.price : undefined}
                                    itemId={pack.id}
                                    name={getPackName(pack)}
                                    removeItem={
                                        shouldDisableforMonthlyRate(pack, financeOption)
                                            ? undefined
                                            : () => {
                                                  dispatch(removePack(pack, true));
                                                  trackBasketRemoveExtra(step);
                                              }
                                    }
                                />
                            ))}
                            {packExtras.map((packExtra) => (
                                <BasketItemRow
                                    key={packExtra.id}
                                    itemId={packExtra.id}
                                    name={packExtra.name}
                                    price={showPrices ? packExtra.price : undefined}
                                    removeItem={() => {
                                        dispatch(
                                            removePackExtra(
                                                packs.find((pack) => find(packExtra.id, pack.equipment))!,
                                                packExtra,
                                                true,
                                            ),
                                        );
                                        trackBasketRemoveExtra(step);
                                    }}
                                />
                            ))}
                        </Styles.BasketList>
                    </BasketItemWrapper>
                )}

                {extras.length > 0 && (
                    <BasketItemWrapper
                        sectionTitleLabel={getBnbLabel({ commonSettings, bnbSettings }, "basketExtras")}
                        totalPrice={totalExtraPrice}
                        itemCount={extras.length}
                        trackExpand={() => trackBasketExpandPanel(step, "expand-extras")}
                    >
                        <Styles.BasketList>
                            {extras.map((extra) => (
                                <BasketItemRow
                                    key={extra.id}
                                    itemId={extra.id}
                                    name={getExtraName(extra)}
                                    price={showPrices ? getPriceFromExtra(extra) : undefined}
                                    removeItem={() => {
                                        dispatch(removeExtra(extra, false, true));
                                        trackBasketRemoveExtra(step);
                                    }}
                                />
                            ))}
                        </Styles.BasketList>
                    </BasketItemWrapper>
                )}
            </div>
        </>
    );
};

export default BasketItems;
